/**
 * Invoice Slice for Wadakaroo
 *
 * Manages state for workshop invoices:
 * - CRUD operations
 * - Payment recording
 * - Invoice lifecycle (send, void)
 * - Search, filtering, and pagination
 * - Line item management (add adjustments, edit, delete)
 * - Detail panel (unified view/edit for all statuses)
 * - PDF download (per-invoice loading state)
 * - Advanced backend search (customer, vehicle, date range)
 * - Invoice archive (reconciled invoices synced to accounting)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getInvoicesApi,
    getInvoiceApi,
    getInvoiceByBookingApi,
    createInvoiceApi,
    updateInvoiceApi,
    sendInvoiceApi,
    voidInvoiceApi,
    reconcileInvoiceApi,
    reconcileInvoicesBatchApi,
    recordPaymentApi,
    getInvoicesByStatusApi,
    getOutstandingInvoicesApi,
    searchInvoicesApi,
    advancedSearchInvoicesApi,
    getOverdueInvoicesApi,
    addLineItemApi,
    updateLineItemApi,
    deleteLineItemApi,
    getLineItemTypesApi,
    getPaymentMethodsApi,
    openInvoicePdfInTab,
    saveInvoicePdf,
    getInvoiceArchiveApi,
    searchInvoiceArchiveApi,
    getAnnualIncomeApi,
    getMonthlyIncomeApi,
    getWeeklyIncomeApi,
    getIncomeYearsApi,
    getSalesTargetsApi,
    setSalesTargetApi,
    clearSalesTargetApi,
} from '../../services/invoiceApi';

// ============================================
// Async Thunks - Fetch Operations
// ============================================

export const fetchInvoices = createAsyncThunk(
    'invoices/fetchAll',
    async ({ page = 0, size = 8 } = {}, { rejectWithValue }) => {
        try {
            return await getInvoicesApi({ page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices');
        }
    }
);

export const fetchInvoice = createAsyncThunk(
    'invoices/fetchOne',
    async (uuid, { rejectWithValue }) => {
        try {
            return await getInvoiceApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice');
        }
    }
);

export const fetchInvoiceByBooking = createAsyncThunk(
    'invoices/fetchByBooking',
    async (bookingUuid, { rejectWithValue }) => {
        try {
            return await getInvoiceByBookingApi(bookingUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice for booking');
        }
    }
);

export const fetchInvoicesByStatus = createAsyncThunk(
    'invoices/fetchByStatus',
    async ({ status, page = 0, size = 8 }, { rejectWithValue }) => {
        try {
            return await getInvoicesByStatusApi(status, { page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoices by status');
        }
    }
);

/**
 * Fetch outstanding invoices — PARTIALLY_PAID + TRADE_CREDIT combined.
 * Backs the "Partial" tab. Reuses the byStatus reducer cases since the
 * payload shape (Page<InvoiceSummaryResponse>) is identical.
 */
export const fetchOutstandingInvoices = createAsyncThunk(
    'invoices/fetchOutstanding',
    async ({ page = 0, size = 8 } = {}, { rejectWithValue }) => {
        try {
            return await getOutstandingInvoicesApi({ page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch outstanding invoices');
        }
    }
);

export const searchInvoices = createAsyncThunk(
    'invoices/search',
    async ({ query, page = 0, size = 8 }, { rejectWithValue }) => {
        try {
            return await searchInvoicesApi(query, { page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search invoices');
        }
    }
);

/**
 * Advanced backend search — customer name/phone, vehicle rego, date range.
 * Replaces the old serverFilter phone/rego-only approach with a richer filter set.
 */
export const advancedSearchInvoices = createAsyncThunk(
    'invoices/advancedSearch',
    async ({ customerQuery = '', vehicleRego = '', dateFrom = '', dateTo = '',
                dateField = 'ISSUE', status = '', page = 0, size = 8 }, { rejectWithValue }) => {
        try {
            return await advancedSearchInvoicesApi({ customerQuery, vehicleRego, dateFrom,
                dateTo, dateField, status, page, size });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search invoices');
        }
    }
);

export const fetchOverdueInvoices = createAsyncThunk(
    'invoices/fetchOverdue',
    async (_, { rejectWithValue }) => {
        try {
            return await getOverdueInvoicesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch overdue invoices');
        }
    }
);

// ============================================
// Async Thunks - CRUD Operations
// ============================================

export const createInvoice = createAsyncThunk(
    'invoices/create',
    async (data, { rejectWithValue }) => {
        try {
            return await createInvoiceApi(data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create invoice');
        }
    }
);

export const updateInvoice = createAsyncThunk(
    'invoices/update',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await updateInvoiceApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update invoice');
        }
    }
);

// ============================================
// Async Thunks - Invoice Lifecycle
// ============================================

export const sendInvoice = createAsyncThunk(
    'invoices/send',
    async ({ uuid, data = {} }, { rejectWithValue }) => {
        try {
            return await sendInvoiceApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to send invoice');
        }
    }
);

export const voidInvoice = createAsyncThunk(
    'invoices/void',
    async ({ uuid, reason }, { rejectWithValue }) => {
        try {
            return await voidInvoiceApi(uuid, reason);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to void invoice');
        }
    }
);

/**
 * Reconcile a selection of invoices.
 *
 * Resolves even when some invoices failed — partial success is the expected shape, not an
 * error. Only a transport/permission failure rejects. The caller reads `failed` to decide
 * what to tell the user.
 */
export const reconcileInvoicesBatch = createAsyncThunk(
    'invoices/reconcileBatch',
    async (invoiceUuids, { rejectWithValue }) => {
        try {
            return await reconcileInvoicesBatchApi(invoiceUuids);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to reconcile the selected invoices');
        }
    }
);

export const reconcileInvoice = createAsyncThunk(
    'invoices/reconcile',
    async ({ uuid }, { rejectWithValue }) => {
        try {
            return await reconcileInvoiceApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reconcile invoice');
        }
    }
);

// ============================================
// Async Thunks - PDF
// ============================================

/**
 * Opens the invoice PDF in a new browser tab.
 * Tracks per-invoice loading state in pdfLoadingUuid so the button
 * can show a spinner without blocking any other UI.
 */
export const openInvoicePdf = createAsyncThunk(
    'invoices/openPdf',
    async ({ uuid, invoiceNumber }, { rejectWithValue }) => {
        try {
            await openInvoicePdfInTab(uuid);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load PDF');
        }
    }
);

/**
 * Triggers a browser Save-As download of the invoice PDF.
 */
export const downloadInvoicePdf = createAsyncThunk(
    'invoices/downloadPdf',
    async ({ uuid, invoiceNumber }, { rejectWithValue }) => {
        try {
            await saveInvoicePdf(uuid, `${invoiceNumber || uuid}.pdf`);
            return uuid;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to download PDF');
        }
    }
);

// ============================================
// Async Thunks - Payment Operations
// ============================================

export const recordPayment = createAsyncThunk(
    'invoices/recordPayment',
    async ({ uuid, data }, { rejectWithValue }) => {
        try {
            return await recordPaymentApi(uuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to record payment');
        }
    }
);

// ============================================
// Async Thunks - Line Item Operations
// ============================================

export const addLineItem = createAsyncThunk(
    'invoices/addLineItem',
    async ({ invoiceUuid, data }, { rejectWithValue }) => {
        try {
            return await addLineItemApi(invoiceUuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add line item');
        }
    }
);

export const updateLineItem = createAsyncThunk(
    'invoices/updateLineItem',
    async ({ invoiceUuid, lineItemUuid, data }, { rejectWithValue }) => {
        try {
            return await updateLineItemApi(invoiceUuid, lineItemUuid, data);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update line item');
        }
    }
);

export const deleteLineItem = createAsyncThunk(
    'invoices/deleteLineItem',
    async ({ invoiceUuid, lineItemUuid }, { rejectWithValue }) => {
        try {
            return await deleteLineItemApi(invoiceUuid, lineItemUuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete line item');
        }
    }
);

/**
 * Fetch the available line item types from the backend so the
 * "Add Line Item / Adjustment" dropdown is driven by the server enum.
 */
export const fetchLineItemTypes = createAsyncThunk(
    'invoices/fetchLineItemTypes',
    async (_, { rejectWithValue }) => {
        try {
            return await getLineItemTypesApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load line item types');
        }
    }
);

/**
 * Fetch the available payment methods from the backend so the
 * Record Payment dialog dropdown is driven by the server enum.
 */
export const fetchPaymentMethods = createAsyncThunk(
    'invoices/fetchPaymentMethods',
    async (_, { rejectWithValue }) => {
        try {
            return await getPaymentMethodsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load payment methods');
        }
    }
);

// ============================================
// Async Thunks - Invoice Archive
// ============================================

export const fetchInvoiceArchive = createAsyncThunk(
    'invoices/fetchArchive',
    async ({ page = 0, size = 20, dateFrom = '', dateTo = '' } = {}, { rejectWithValue }) => {
        try {
            return await getInvoiceArchiveApi({ page, size, dateFrom, dateTo });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch invoice archive');
        }
    }
);

export const searchInvoiceArchive = createAsyncThunk(
    'invoices/searchArchive',
    async ({ query, page = 0, size = 20, dateFrom = '', dateTo = '' }, { rejectWithValue }) => {
        try {
            return await searchInvoiceArchiveApi(query, { page, size, dateFrom, dateTo });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to search invoice archive');
        }
    }
);

// ============================================
// Initial State
// ============================================

// ============================================
// Async Thunks - Income & Sales Targets
// ============================================

export const fetchAnnualIncome = createAsyncThunk(
    'invoices/fetchAnnualIncome',
    async ({ year, basis = 'FINANCIAL' } = {}, { rejectWithValue }) => {
        try {
            return await getAnnualIncomeApi({ year, basis });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load income totals');
        }
    }
);

export const fetchMonthlyIncome = createAsyncThunk(
    'invoices/fetchMonthlyIncome',
    async ({ year, month } = {}, { rejectWithValue }) => {
        try {
            return await getMonthlyIncomeApi({ year, month });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load monthly income');
        }
    }
);

export const fetchWeeklyIncome = createAsyncThunk(
    'invoices/fetchWeeklyIncome',
    async ({ year, month } = {}, { rejectWithValue }) => {
        try {
            return await getWeeklyIncomeApi({ year, month });
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load weekly income');
        }
    }
);

export const fetchIncomeYears = createAsyncThunk(
    'invoices/fetchIncomeYears',
    async (_, { rejectWithValue }) => {
        try {
            return await getIncomeYearsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load available years');
        }
    }
);

export const fetchSalesTargets = createAsyncThunk(
    'invoices/fetchSalesTargets',
    async (year, { rejectWithValue }) => {
        try {
            return await getSalesTargetsApi(year);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load sales targets');
        }
    }
);

/**
 * Sets a month's target then re-reads the views it affects.
 *
 * The annual summary and the monthly panel both embed the target, so a write that only
 * updated the target list would leave two stale figures on screen. Re-fetching is one
 * extra round trip on a rare action, which is cheaper than reconciling three shapes of
 * derived state by hand.
 */
export const saveSalesTarget = createAsyncThunk(
    'invoices/saveSalesTarget',
    async ({ year, month, targetAmount, applyToRemainingMonths = false, refresh = {} },
           { dispatch, rejectWithValue }) => {
        try {
            const result = await setSalesTargetApi({ year, month, targetAmount, applyToRemainingMonths });
            if (refresh.annual) dispatch(fetchAnnualIncome(refresh.annual));
            if (refresh.monthly) dispatch(fetchMonthlyIncome(refresh.monthly));
            return result;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to save the sales target');
        }
    }
);

export const removeSalesTarget = createAsyncThunk(
    'invoices/removeSalesTarget',
    async ({ year, month, refresh = {} }, { dispatch, rejectWithValue }) => {
        try {
            await clearSalesTargetApi({ year, month });
            if (refresh.annual) dispatch(fetchAnnualIncome(refresh.annual));
            if (refresh.monthly) dispatch(fetchMonthlyIncome(refresh.monthly));
            return { year, month };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to clear the sales target');
        }
    }
);

const initialState = {
    // Invoice data
    invoices: [],
    selectedInvoice: null,
    overdueInvoices: [],

    // Pagination
    pagination: {
        page: 0,
        size: 8,
        totalElements: 0,
        totalPages: 0,
    },

    // Filters
    statusFilter: 'all',
    searchQuery: '',

    // Advanced server-side filter (replaces old customerPhone/vehicleRego)
    serverFilter: {
        customerQuery: '',   // customer name or phone
        vehicleRego: '',
        dateFrom: '',
        dateTo: '',
        active: false,       // true when a search has been applied
    },

    // Loading states
    invoicesLoading: false,
    invoicesError: null,
    detailLoading: false,
    pdfLoadingUuid: null,    // uuid of invoice whose PDF is currently loading
    pdfError: null,

    // Operation states
    operationLoading: false,
    operationError: null,
    operationSuccess: null,

    /** Per-invoice outcomes from the last batch reconcile, for the results panel. */
    bulkReconcileResult: null,

    // Line item type options (fetched once from the backend, reused for the
    // "Add Line Item / Adjustment" dropdown so frontend stays in sync with
    // the QuoteLineItem.LineType enum).
    lineItemTypes: [],
    lineItemTypesLoading: false,
    paymentMethods: [],
    paymentMethodsLoading: false,

    // UI State
    showCreateModal: false,
    showDetailPanel: false,
    showEditModal: false,
    showViewModal: false,
    showPaymentModal: false,
    showVoidConfirm: false,
    showSendConfirm: false,

    // Invoice Archive
    archive: [],
    archivePagination: {
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
    },
    archiveTotalAmount: 0,
    archiveLoading: false,
    archiveError: null,

    // ── Income & sales targets ────────────────────────────────────────────
    // Every figure in annualIncome / monthlyIncome arrives pre-computed from the
    // backend. Nothing in the UI recalculates a total, percentage or variance.
    annualIncome: null,
    annualIncomeLoading: false,
    annualIncomeError: null,

    monthlyIncome: null,
    weeklyIncome: null,
    weeklyIncomeLoading: false,
    weeklyIncomeError: null,
    monthlyIncomeLoading: false,
    monthlyIncomeError: null,

    /** { calendarYears: number[], financialYears: number[] } — newest first. */
    incomeYears: null,

    salesTargets: [],
    targetSaving: false,
    targetError: null,
};

// ============================================
// Helper — update selectedInvoice and sync list
// ============================================

const applyInvoiceUpdate = (state, updatedInvoice) => {
    const index = state.invoices.findIndex(i => i.uuid === updatedInvoice.uuid);
    if (index !== -1) {
        state.invoices[index] = { ...state.invoices[index], ...updatedInvoice };
    }
    state.selectedInvoice = updatedInvoice;
};

// ============================================
// Pagination helper
// ============================================

const applyPaginatedResponse = (state, payload) => {
    state.invoicesLoading = false;
    state.invoices = payload.content || [];
    state.pagination = {
        page: payload.pageable?.number ?? 0,
        size: payload.pageable?.size ?? 8,
        totalElements: payload.totalSize ?? payload.totalElements ?? 0,
        totalPages: payload.totalPages ?? Math.ceil(
            (payload.totalSize ?? payload.totalElements ?? 0) / (payload.pageable?.size ?? 8)
        ),
    };
};

const applyArchivePaginatedResponse = (state, payload) => {
    // Backend now returns { page: { content, pageable, ... }, totalAmount }
    const pageData = payload.page || payload;  // fallback for backward compat
    const totalAmount = payload.totalAmount ?? 0;

    state.archiveLoading = false;
    state.archive = pageData.content || [];
    state.archiveTotalAmount = totalAmount;
    state.archivePagination = {
        page: pageData.pageable?.number ?? 0,
        size: pageData.pageable?.size ?? 20,
        totalElements: pageData.totalSize ?? pageData.totalElements ?? 0,
        totalPages: pageData.totalPages ?? Math.ceil(
            (pageData.totalSize ?? pageData.totalElements ?? 0) / (pageData.pageable?.size ?? 20)
        ),
    };
};

// ============================================
// Slice
// ============================================

const invoiceSlice = createSlice({
    name: 'invoices',
    initialState,
    reducers: {
        // Filter actions
        setStatusFilter: (state, action) => {
            state.statusFilter = action.payload;
            state.pagination.page = 0;
            // Clear advanced search when switching status tabs
            state.serverFilter.active = false;
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },

        // Advanced server filter
        setServerFilter: (state, action) => {
            state.serverFilter = { ...state.serverFilter, ...action.payload };
            state.pagination.page = 0;
        },
        clearServerFilter: (state) => {
            state.serverFilter = { customerQuery: '', vehicleRego: '', dateFrom: '', dateTo: '', active: false };
            state.pagination.page = 0;
        },

        // Pagination actions
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pagination.size = action.payload;
            state.pagination.page = 0;
        },

        // Archive pagination
        setArchivePage: (state, action) => {
            state.archivePagination.page = action.payload;
        },

        // Modal actions
        openCreateModal: (state) => { state.showCreateModal = true; },
        closeCreateModal: (state) => { state.showCreateModal = false; },

        openDetailPanel: (state, action) => {
            state.selectedInvoice = action.payload;
            state.showDetailPanel = true;
            state.showEditModal = true;
            state.showViewModal = true;
        },
        closeDetailPanel: (state) => {
            state.showDetailPanel = false;
            state.showEditModal = false;
            state.showViewModal = false;
            state.selectedInvoice = null;
            state.operationError = null;
        },

        // Backward-compat aliases
        openEditModal: (state, action) => {
            state.selectedInvoice = action.payload;
            state.showDetailPanel = true;
            state.showEditModal = true;
            state.showViewModal = true;
        },
        closeEditModal: (state) => {
            state.showDetailPanel = false;
            state.showEditModal = false;
            state.showViewModal = false;
            state.selectedInvoice = null;
        },
        openViewModal: (state, action) => {
            state.selectedInvoice = action.payload;
            state.showDetailPanel = true;
            state.showEditModal = true;
            state.showViewModal = true;
        },
        closeViewModal: (state) => {
            state.showDetailPanel = false;
            state.showEditModal = false;
            state.showViewModal = false;
            state.selectedInvoice = null;
        },

        openPaymentModal: (state, action) => {
            state.showPaymentModal = true;
            state.selectedInvoice = action.payload;
        },
        closePaymentModal: (state) => {
            state.showPaymentModal = false;
            state.selectedInvoice = null;
        },
        openVoidConfirm: (state, action) => {
            state.showVoidConfirm = true;
            state.selectedInvoice = action.payload;
        },
        closeVoidConfirm: (state) => { state.showVoidConfirm = false; },
        openSendConfirm: (state, action) => {
            state.showSendConfirm = true;
            state.selectedInvoice = action.payload;
        },
        closeSendConfirm: (state) => { state.showSendConfirm = false; },

        selectInvoice: (state, action) => { state.selectedInvoice = action.payload; },
        clearSelectedInvoice: (state) => { state.selectedInvoice = null; },

        clearOperationStatus: (state) => {
            state.operationError = null;
            state.operationSuccess = null;
            state.pdfError = null;
        },
    },
    extraReducers: (builder) => {
        // ==================== Fetch All ====================
        builder
            .addCase(fetchInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(fetchInvoices.fulfilled, (state, action) => applyPaginatedResponse(state, action.payload))
            .addCase(fetchInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // ==================== Fetch By Status ====================
            .addCase(fetchInvoicesByStatus.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(fetchInvoicesByStatus.fulfilled, (state, action) => applyPaginatedResponse(state, action.payload))
            .addCase(fetchInvoicesByStatus.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // ==================== Fetch Outstanding (Partial tab: PARTIALLY_PAID + TRADE_CREDIT) ====================
            .addCase(fetchOutstandingInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(fetchOutstandingInvoices.fulfilled, (state, action) => applyPaginatedResponse(state, action.payload))
            .addCase(fetchOutstandingInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // ==================== Search ====================
            .addCase(searchInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(searchInvoices.fulfilled, (state, action) => applyPaginatedResponse(state, action.payload))
            .addCase(searchInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // ==================== Advanced Search ====================
            .addCase(advancedSearchInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(advancedSearchInvoices.fulfilled, (state, action) => {
                applyPaginatedResponse(state, action.payload);
                state.serverFilter.active = true;
            })
            .addCase(advancedSearchInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // ==================== Fetch Overdue ====================
            .addCase(fetchOverdueInvoices.pending, (state) => { state.invoicesLoading = true; })
            .addCase(fetchOverdueInvoices.fulfilled, (state, action) => {
                state.invoicesLoading = false;
                state.overdueInvoices = action.payload;
            })
            .addCase(fetchOverdueInvoices.rejected, (state) => { state.invoicesLoading = false; })

            // ==================== Fetch Single (full detail) ====================
            .addCase(fetchInvoice.pending, (state) => {
                state.detailLoading = true;
                state.operationError = null;
                // 🔑 The panel opens NOW, on the shapes it is about to fill. It used to
                // wait for the invoice, so following an invoice link from a job left the
                // screen unchanged for as long as the fetch took — which reads as a link
                // that did nothing.
                state.showDetailPanel = true;
            })
            .addCase(fetchInvoice.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.selectedInvoice = action.payload;
                state.showDetailPanel = true;
                state.showEditModal = true;
                state.showViewModal = true;
            })
            .addCase(fetchInvoice.rejected, (state, action) => {
                state.detailLoading = false;
                state.operationError = action.payload;
                // Nothing arrived, so there is nothing to show: an empty panel would
                // sit there holding shapes that will never be filled.
                if (!state.selectedInvoice) {
                    state.showDetailPanel = false;
                }
            })

            // ==================== Create ====================
            .addCase(createInvoice.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(createInvoice.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.invoices.unshift(action.payload);
                state.showCreateModal = false;
                state.operationSuccess = 'Invoice created successfully';
            })
            .addCase(createInvoice.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update ====================
            .addCase(updateInvoice.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateInvoice.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.operationSuccess = 'Invoice updated';
            })
            .addCase(updateInvoice.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Send ====================
            .addCase(sendInvoice.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(sendInvoice.fulfilled, (state, action) => {
                state.operationLoading = false;
                // Remove from DRAFT section — applyInvoiceUpdate syncs status to SENT
                applyInvoiceUpdate(state, action.payload);
                state.showSendConfirm = false;
                state.operationSuccess = `Invoice ${action.payload.invoiceNumber} sent to ${action.payload.customerEmail}`;
            })
            .addCase(sendInvoice.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Void ====================
            .addCase(voidInvoice.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(voidInvoice.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.showVoidConfirm = false;
                state.operationSuccess = 'Invoice voided';
            })
            .addCase(voidInvoice.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Bulk reconcile ====================
            .addCase(reconcileInvoicesBatch.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
                state.operationSuccess = null;
            })
            .addCase(reconcileInvoicesBatch.fulfilled, (state, action) => {
                const { succeeded = 0, failed = 0, notAttempted = 0 } = action.payload ?? {};
                state.operationLoading = false;
                // Reported honestly. A run that halted is NOT a success message, and the
                // untouched remainder is the number the user acts on next.
                state.operationSuccess = failed === 0
                    ? `${succeeded} invoice${succeeded === 1 ? '' : 's'} reconciled`
                    : `${succeeded} reconciled, then stopped — ${notAttempted} not attempted`;
                state.bulkReconcileResult = action.payload ?? null;
            })
            .addCase(reconcileInvoicesBatch.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Reconcile ====================
            .addCase(reconcileInvoice.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(reconcileInvoice.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.operationSuccess = 'Invoice reconciled';
            })
            .addCase(reconcileInvoice.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== PDF ====================
            .addCase(openInvoicePdf.pending, (state, action) => {
                state.pdfLoadingUuid = action.meta.arg.uuid;
                state.pdfError = null;
            })
            .addCase(openInvoicePdf.fulfilled, (state) => {
                state.pdfLoadingUuid = null;
            })
            .addCase(openInvoicePdf.rejected, (state, action) => {
                state.pdfLoadingUuid = null;
                state.pdfError = action.payload;
                state.operationError = action.payload;
            })

            .addCase(downloadInvoicePdf.pending, (state, action) => {
                state.pdfLoadingUuid = action.meta.arg.uuid;
                state.pdfError = null;
            })
            .addCase(downloadInvoicePdf.fulfilled, (state) => {
                state.pdfLoadingUuid = null;
            })
            .addCase(downloadInvoicePdf.rejected, (state, action) => {
                state.pdfLoadingUuid = null;
                state.pdfError = action.payload;
                state.operationError = action.payload;
            })

            // ==================== Record Payment ====================
            .addCase(recordPayment.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(recordPayment.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.showPaymentModal = false;
                state.operationSuccess = 'Payment recorded';
            })
            .addCase(recordPayment.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Add Line Item ====================
            .addCase(addLineItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(addLineItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.operationSuccess = 'Line item added';
            })
            .addCase(addLineItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Update Line Item ====================
            .addCase(updateLineItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(updateLineItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.operationSuccess = 'Line item updated';
            })
            .addCase(updateLineItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Delete Line Item ====================
            .addCase(deleteLineItem.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(deleteLineItem.fulfilled, (state, action) => {
                state.operationLoading = false;
                applyInvoiceUpdate(state, action.payload);
                state.operationSuccess = 'Line item removed';
            })
            .addCase(deleteLineItem.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            })

            // ==================== Fetch Line Item Types ====================
            .addCase(fetchLineItemTypes.pending, (state) => {
                state.lineItemTypesLoading = true;
            })
            .addCase(fetchLineItemTypes.fulfilled, (state, action) => {
                state.lineItemTypesLoading = false;
                state.lineItemTypes = action.payload || [];
            })
            .addCase(fetchLineItemTypes.rejected, (state) => {
                state.lineItemTypesLoading = false;
                // Soft-fail — UI falls back to a minimal default list.
            })

            // ==================== Fetch Payment Methods ====================
            .addCase(fetchPaymentMethods.pending, (state) => {
                state.paymentMethodsLoading = true;
            })
            .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
                state.paymentMethodsLoading = false;
                state.paymentMethods = action.payload || [];
            })
            .addCase(fetchPaymentMethods.rejected, (state) => {
                state.paymentMethodsLoading = false;
                // Soft-fail — UI falls back to a minimal default list.
            })

            // ==================== Invoice Archive ====================
            .addCase(fetchInvoiceArchive.pending, (state) => {
                state.archiveLoading = true;
                state.archiveError = null;
            })
            .addCase(fetchInvoiceArchive.fulfilled, (state, action) => applyArchivePaginatedResponse(state, action.payload))
            .addCase(fetchInvoiceArchive.rejected, (state, action) => {
                state.archiveLoading = false;
                state.archiveError = action.payload;
            })

            .addCase(searchInvoiceArchive.pending, (state) => {
                state.archiveLoading = true;
                state.archiveError = null;
            })
            .addCase(searchInvoiceArchive.fulfilled, (state, action) => applyArchivePaginatedResponse(state, action.payload))
            .addCase(searchInvoiceArchive.rejected, (state, action) => {
                state.archiveLoading = false;
                state.archiveError = action.payload;
            })

            // ==================== Annual income (tax view) ====================
            .addCase(fetchAnnualIncome.pending, (state) => {
                state.annualIncomeLoading = true;
                state.annualIncomeError = null;
            })
            .addCase(fetchAnnualIncome.fulfilled, (state, action) => {
                state.annualIncomeLoading = false;
                state.annualIncome = action.payload;
            })
            .addCase(fetchAnnualIncome.rejected, (state, action) => {
                state.annualIncomeLoading = false;
                state.annualIncomeError = action.payload;
                // Cleared deliberately: leaving last year's totals on screen under a new
                // year's heading would be worse than showing an error.
                state.annualIncome = null;
            })

            // ==================== This month vs target ====================
            .addCase(fetchMonthlyIncome.pending, (state) => {
                state.monthlyIncomeLoading = true;
                state.monthlyIncomeError = null;
            })
            .addCase(fetchMonthlyIncome.fulfilled, (state, action) => {
                state.monthlyIncomeLoading = false;
                state.monthlyIncome = action.payload;
            })
            .addCase(fetchMonthlyIncome.rejected, (state, action) => {
                state.monthlyIncomeLoading = false;
                state.monthlyIncomeError = action.payload;
                state.monthlyIncome = null;
            })
            .addCase(fetchWeeklyIncome.pending, (state) => {
                state.weeklyIncomeLoading = true;
                state.weeklyIncomeError = null;
            })
            .addCase(fetchWeeklyIncome.fulfilled, (state, action) => {
                state.weeklyIncomeLoading = false;
                state.weeklyIncome = action.payload;
            })
            .addCase(fetchWeeklyIncome.rejected, (state, action) => {
                state.weeklyIncomeLoading = false;
                state.weeklyIncomeError = action.payload;
            })

            // ==================== Year picker ====================
            .addCase(fetchIncomeYears.fulfilled, (state, action) => {
                state.incomeYears = action.payload;
            })
            // Soft-fail: the picker falls back to the current year only.
            .addCase(fetchIncomeYears.rejected, () => {})

            // ==================== Sales targets ====================
            .addCase(fetchSalesTargets.fulfilled, (state, action) => {
                state.salesTargets = action.payload || [];
            })

            .addCase(saveSalesTarget.pending, (state) => {
                state.targetSaving = true;
                state.targetError = null;
            })
            .addCase(saveSalesTarget.fulfilled, (state, action) => {
                state.targetSaving = false;
                const written = [
                    action.payload?.target,
                    ...(action.payload?.alsoApplied || []),
                ].filter(Boolean);
                // Merge rather than replace: the list is per calendar year and a write can
                // land several months at once via applyToRemainingMonths.
                written.forEach((t) => {
                    const i = state.salesTargets.findIndex(
                        (x) => x.year === t.year && x.month === t.month);
                    if (i === -1) state.salesTargets.push(t);
                    else state.salesTargets[i] = t;
                });
                state.salesTargets.sort((a, b) => (a.year - b.year) || (a.month - b.month));
                state.operationSuccess = written.length > 1
                    ? `Sales target set for ${written.length} months`
                    : 'Sales target saved';
            })
            .addCase(saveSalesTarget.rejected, (state, action) => {
                state.targetSaving = false;
                state.targetError = action.payload;
            })

            .addCase(removeSalesTarget.pending, (state) => {
                state.targetSaving = true;
                state.targetError = null;
            })
            .addCase(removeSalesTarget.fulfilled, (state, action) => {
                state.targetSaving = false;
                state.salesTargets = state.salesTargets.filter(
                    (t) => !(t.year === action.payload.year && t.month === action.payload.month));
                state.operationSuccess = 'Sales target cleared';
            })
            .addCase(removeSalesTarget.rejected, (state, action) => {
                state.targetSaving = false;
                state.targetError = action.payload;
            });
    },
});

// ============================================
// Selectors
// ============================================

export const selectInvoicesGroupedByStatus = (state) => {
    const grouped = { DRAFT: [], SENT: [], PARTIALLY_PAID: [], PAID: [], OVERDUE: [], VOID: [] };
    state.invoices.invoices.forEach(invoice => {
        if (grouped[invoice.status]) grouped[invoice.status].push(invoice);
    });
    return grouped;
};

export const selectInvoiceStats = (state) => {
    const invoices = state.invoices.invoices;
    return {
        totalPaid: invoices.filter(i => i.status === 'PAID').reduce((s, i) => s + (i.totalAmount || 0), 0),
        totalOutstanding: invoices.filter(i => ['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(i.status)).reduce((s, i) => s + (i.balanceDue || 0), 0),
        totalOverdue: invoices.filter(i => i.status === 'OVERDUE').reduce((s, i) => s + (i.balanceDue || 0), 0),
        draftCount: invoices.filter(i => i.status === 'DRAFT').length,
    };
};

export const selectFilteredInvoices = (state) => {
    const { invoices, searchQuery } = state.invoices;
    let filtered = [...invoices];
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(inv =>
            inv.invoiceNumber?.toLowerCase().includes(query) ||
            inv.customerName?.toLowerCase().includes(query) ||
            inv.bookingCode?.toLowerCase().includes(query) ||
            inv.vehicleRego?.toLowerCase().includes(query)
        );
    }
    filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return filtered;
};

// ============================================
// Exports
// ============================================

export const {
    setStatusFilter,
    setSearchQuery,
    setServerFilter,
    clearServerFilter,
    setPage,
    setPageSize,
    setArchivePage,
    openCreateModal,
    closeCreateModal,
    openDetailPanel,
    closeDetailPanel,
    openEditModal,
    closeEditModal,
    openViewModal,
    closeViewModal,
    openPaymentModal,
    closePaymentModal,
    openVoidConfirm,
    closeVoidConfirm,
    openSendConfirm,
    closeSendConfirm,
    selectInvoice,
    clearSelectedInvoice,
    clearOperationStatus,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;